import React, { useState, useEffect, useMemo } from "react";
import { usePostHog } from "posthog-js/react";
import { Minus, Plus, ChevronDown, ChevronUp, Undo2, Redo2 } from "lucide-react";
import cn from "classnames";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { Cursor } from "components/icons/components/Cursor";
import { Ellipse } from "components/icons/components/Ellipse";
import { Saving } from "components/Saving";
import { MAP_TYPES } from "pages/constants";
import {
  calculateMinCircleRadius,
  calculateMaxCircleRadius,
  updateSelectedRoutesOnDataLayer,
} from "components/audience-map/utils";
import { useStore } from "store";
import { getSelectedRoutesFromDistributionLocations } from "../AudienceMapEDDM/utils";

const radiusButtonsLabels = {
  plus: "+",
  minus: "-",
};

const radiusOptions = [1, 2, 5, 10];

export const EDDMToolbar = ({
  radius,
  setRadius,
  isSaving,
  drawingMode,
  setDrawingMode,
  setShouldAutoSave,
  circleSelectorRadius,
  setCircleSelectorRadius,
  setIsUndoOrRedo,
  setSelectedRoutes,
  fetchRoutes,
  originator,
  caretaker,
  fetchRoutesAndUpdateMap,
  googleMap,
  setShouldHideStatistics,
}) => {
  const posthog = usePostHog();
  const posthogEventTracker = new EmitAnalyticsEvent(posthog);
  const {
    campaign: { id: campaignId },
    user,
    client: { name: clientName },
    updateMap,
    setDistributionLocations,
    distributionLocations,
  } = useStore();
  const [openSlider, setOpenSlider] = useState(false);

  const focusedLocation = useMemo(
    () => distributionLocations.find((location) => location.isFocused),
    [distributionLocations]
  );
  const disabledUndo = isSaving || caretaker.isUndoDisabled(campaignId);
  const disabledRedo = isSaving || caretaker.isRedoDisabled(campaignId);

  const handleMapRadiusChange = (value) => {
    const currentRadiusOption = radiusOptions.findIndex((r) => r === radius);
    if (value === radiusButtonsLabels.plus && radius < 10) {
      setShouldAutoSave(true);
      setIsUndoOrRedo(false);
      const newRadius = radiusOptions[currentRadiusOption + 1];

      posthogEventTracker.run({
        eventName: "Changed map radius using buttons",
        userId: user?.id,
        clientName,
        device: navigator.userAgent,
        additionalPayload: { mapRadius: newRadius },
      });

      setRadius(newRadius);
      fetchRoutesAndUpdateMap(focusedLocation, newRadius);
    } else if (value === radiusButtonsLabels.minus && radius > 1) {
      setShouldAutoSave(true);
      setIsUndoOrRedo(false);
      const newRadius = radiusOptions[currentRadiusOption - 1];

      posthogEventTracker.run({
        eventName: "Changed map radius using buttons",
        userId: user?.id,
        clientName,
        device: navigator.userAgent,
        additionalPayload: { mapRadius: newRadius },
      });
      setRadius(newRadius);
      fetchRoutesAndUpdateMap(focusedLocation, newRadius);
    }
  };

  const handleUndo = () => {
    if (disabledUndo) return;
    setIsUndoOrRedo(true);
    setShouldAutoSave(true);
    caretaker.undo(campaignId);

    posthogEventTracker.run({
      eventName: "Action undone on the map",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: { selectionType: drawingMode ? "circle" : "individual" },
    });
    handleUndoOrRedo();
  };

  const handleRedo = () => {
    if (disabledRedo) return;
    setIsUndoOrRedo(true);
    setShouldAutoSave(true);
    caretaker.redo(campaignId);

    posthogEventTracker.run({
      eventName: "Action re-done on the map",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: { selectionType: drawingMode ? "circle" : "individual" },
    });
    handleUndoOrRedo();
  };

  const handleUndoOrRedo = () => {
    const newDistributionLocations = originator.getState();
    setDistributionLocations(newDistributionLocations);

    const newFocusedLocation = newDistributionLocations.find((location) => location.isFocused);
    const newSelectedRoutes = getSelectedRoutesFromDistributionLocations(newDistributionLocations);
    setSelectedRoutes(newSelectedRoutes);
    if (
      !newFocusedLocation ||
      (newFocusedLocation?.name === focusedLocation?.name && newFocusedLocation?.radius === focusedLocation?.radius)
    ) {
      updateSelectedRoutesOnDataLayer({ dataLayer: googleMap?.data, selectedRoutes: newSelectedRoutes });
      return;
    }
    setRadius(newFocusedLocation.radius);
    updateMap({
      loading: true,
      loadingError: null,
      loadingNotice: null,
      center: { lat: newFocusedLocation.lat, lng: newFocusedLocation.lng },
    });
    Promise.resolve(
      fetchRoutes({
        center: [newFocusedLocation.lng, newFocusedLocation.lat],
        radius: newFocusedLocation.radius * 1609.34,
      })
    ).catch((error) => {
      // fetchRoutes already clears `loading` in a finally; this only stops an
      // unexpected throw from surfacing as an unhandled rejection.
      console.error("EDDM carrier-route lookup failed:", error);
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      if (isMac ? event.metaKey : event.ctrlKey) {
        if (event.key.toLowerCase() === "z" && !event.shiftKey) {
          // CMD/CTRL + Z
          event.preventDefault();
          handleUndo();
        } else if ((event.key.toLowerCase() === "z" && event.shiftKey) || event.key.toLowerCase() === "y") {
          // CMD/CTRL + SHIFT + Z
          event.preventDefault();
          handleRedo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  const increaseZoomDisabled = radius === 10;
  const decreaseZoomDisabled = radius === 1;
  const cursorSelected = drawingMode === null;
  const circleSelected = drawingMode === MAP_TYPES.CIRCLE;
  const minSliderValue = calculateMinCircleRadius(radius);
  const maxSliderValue = calculateMaxCircleRadius(radius);

  return (
    <div
      className="cb-v2-root box-border absolute bottom-[27.5px] left-[35vw] flex h-[78px] w-fit items-center justify-around rounded-lg border border-solid border-border bg-card px-4 py-3 text-foreground shadow-sm min-[1320px]:left-[calc(50vw-300px)]"
      onMouseEnter={() => setShouldHideStatistics(true)}
      onMouseLeave={() => setShouldHideStatistics(false)}
    >
      <div className="mr-[15px] flex h-full w-[190px] flex-col items-center justify-between text-center text-sm">
        <span>Show routes in a radius of</span>
        <div className="flex items-center">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleMapRadiusChange(radiusButtonsLabels.minus)}
              disabled={decreaseZoomDisabled}
              data-cypress-id="eddm-toolbar-radius-decrease"
              className={cn(
                "flex h-[30px] w-[41px] items-center justify-center rounded-l-lg border border-solid border-border bg-transparent",
                decreaseZoomDisabled ? "cursor-not-allowed text-muted-foreground" : "cursor-pointer text-foreground"
              )}
            >
              <Minus className="h-4 w-4" />
            </button>

            <span>{radius} miles</span>

            <button
              type="button"
              onClick={() => handleMapRadiusChange(radiusButtonsLabels.plus)}
              disabled={increaseZoomDisabled}
              data-cypress-id="eddm-toolbar-radius-increase"
              className={cn(
                "flex h-[30px] w-[41px] items-center justify-center rounded-r-lg border border-solid border-border bg-transparent",
                increaseZoomDisabled ? "cursor-not-allowed text-muted-foreground" : "cursor-pointer text-foreground"
              )}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="h-9 border-0 border-r border-solid border-border" />

      <div className="ml-[15px] mr-[15px] flex h-full w-[165px] flex-col justify-between text-sm">
        <span>Selection</span>
        <div className="flex h-[34px] w-fit items-center rounded-lg border border-solid border-border">
          <button
            type="button"
            data-cypress-id="eddm-toolbar-select-route"
            className={cn(
              "flex h-full w-[77px] cursor-pointer items-center justify-center gap-2.5 rounded-lg",
              cursorSelected ? "bg-[#222336] text-white" : "bg-transparent text-[#222336] dark:text-foreground"
            )}
            onClick={() => {
              posthogEventTracker.run({
                eventName: "Used route individual selection",
                userId: user?.id,
                clientName,
                device: navigator.userAgent,
              });
              setDrawingMode(null);
            }}
          >
            <Cursor stroke="currentColor" /> <span>Route</span>
          </button>
          <div className="mx-[5px] h-4 border-0 border-r border-solid border-border" />
          <button
            type="button"
            data-cypress-id="eddm-toolbar-select-area"
            className={cn(
              "flex h-full w-[77px] cursor-pointer items-center justify-center gap-2.5 rounded-lg",
              circleSelected ? "bg-[#222336] text-white" : "bg-transparent text-[#222336] dark:text-foreground"
            )}
            onClick={() => {
              posthogEventTracker.run({
                eventName: "Used multiple routes selection",
                userId: user?.id,
                clientName,
                device: navigator.userAgent,
              });
              setDrawingMode(MAP_TYPES.CIRCLE);
            }}
          >
            <Ellipse stroke="currentColor" />
            <span>Area</span>
          </button>
        </div>
      </div>
      {circleSelected && (
        <div className="mr-[15px] flex h-full flex-col justify-between text-sm">
          <span>Selector size</span>
          {openSlider && (
            <div className="absolute bottom-[51px] left-[320px] flex h-[62px] w-[184px] flex-col items-center rounded-lg border border-solid border-border bg-card px-5 py-2.5 shadow-sm">
              <div className="flex w-full items-center justify-between">
                <span>{`${minSliderValue} mi`}</span>
                <span>{`${maxSliderValue} mi`}</span>
              </div>
              <input
                type="range"
                value={circleSelectorRadius}
                min={minSliderValue}
                step={0.25}
                max={maxSliderValue}
                data-cypress-id="eddm-toolbar-selector-size-slider"
                className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-primary dark:bg-accent"
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value === circleSelectorRadius) return;
                  posthogEventTracker.run({
                    eventName: "Used selector size adjustment",
                    userId: user?.id,
                    clientName,
                    device: navigator.userAgent,
                    additionalPayload: {
                      selectorSize: `${value}mi`,
                      mapRadius: `${radius} mile${radius > 1 ? "s" : ""}`,
                    },
                  });
                  setCircleSelectorRadius(value);
                }}
                onBlur={() => setOpenSlider(false)}
              />
            </div>
          )}
          <button
            type="button"
            data-cypress-id="eddm-toolbar-selector-size-toggle"
            className="flex cursor-pointer items-center rounded-lg border border-solid border-border bg-transparent text-foreground"
            onClick={() => setOpenSlider(!openSlider)}
          >
            <span className="flex h-full w-[57px] items-center justify-center">{circleSelectorRadius} mi</span>
            <div className="mx-[5px] h-4 border-0 border-r border-solid border-border" />
            {openSlider ? <ChevronUp className="mr-[7px] h-4 w-4" /> : <ChevronDown className="mr-[7px] h-4 w-4" />}
          </button>
        </div>
      )}

      <div className="mr-[15px] flex h-full items-end pb-[5px]">
        <Saving saving={isSaving} loadingText="Saving changes" successText="Campaign saved" />
      </div>

      <div className="h-9 border-0 border-r border-solid border-border" />

      <div className="ml-[15px] flex h-full items-end pb-1">
        <div className="flex w-[52px] items-center justify-between">
          <button
            type="button"
            onClick={handleUndo}
            disabled={disabledUndo}
            aria-label="Undo"
            data-cypress-id="eddm-toolbar-undo"
            className={cn(
              "flex items-center justify-center bg-transparent p-0",
              disabledUndo
                ? "cursor-default text-[#E4E4EA] dark:text-muted"
                : "cursor-pointer text-[#4F5B94] dark:text-muted-foreground"
            )}
          >
            <Undo2 className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={disabledRedo}
            aria-label="Redo"
            data-cypress-id="eddm-toolbar-redo"
            className={cn(
              "flex items-center justify-center bg-transparent p-0",
              disabledRedo
                ? "cursor-default text-[#E4E4EA] dark:text-muted"
                : "cursor-pointer text-[#4F5B94] dark:text-muted-foreground"
            )}
          >
            <Redo2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
